import { useFindMany } from "@gadgetinc/react";
import { useEffect, useState } from "react";
import { api } from "../../api";
import LoadingSpinner from "../LoadingSpinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../tailwind/table";

export function FlowButtonList({ scenarioId, campaignId, filterDates, customerId, campaignCreatedAt }) {
  const [sentCounts, setSentCounts] = useState({});
  const [answeredCounts, setAnsweredCounts] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const [{ data: flowButtonNodes, fetching: fetchingNodes }] = useFindMany(
    api.flowNode,
    {
      filter: [
        { scenarioId: { equals: scenarioId } },
        { type: { equals: "flow_button" } },
      ],
      select: {
        id: true,
        flowButton: {
          id: true,
        },
        parentNode: {
          whatsappTemplate: {
            id: true,
            whatsappTemplateName: true,
          },
        },
      },
    },
  );

  useEffect(() => {
    if (!customerId || !campaignId || !filterDates) return;

    const effectiveStartDate = filterDates.startDate ? new Date(filterDates.startDate) : (campaignCreatedAt ? new Date(campaignCreatedAt) : new Date(0));
    const effectiveEndDate = filterDates.endDate ? new Date(filterDates.endDate) : new Date();

    setIsLoading(true);

    Promise.all([
      api.getFlowButtonSentByCampaignId({
        customerId,
        campaignId,
        from: effectiveStartDate,
        to: effectiveEndDate,
      }),
      api.getFlowButtonAnsweredByCampaignId({
        customerId,
        campaignId,
        from: effectiveStartDate,
        to: effectiveEndDate,
      }),
    ])
      .then(([sentData, answeredData]) => {
        const sentMap = {};
        for (const row of sentData?.messages ?? []) {
          if (row.whatsappTemplateId) {
            sentMap[row.whatsappTemplateId] = row.sent ?? 0;
          }
        }
        setSentCounts(sentMap);

        const answeredMap = {};
        for (const row of answeredData?.flowButtonResponses ?? []) {
          if (row.flowButtonId) {
            answeredMap[row.flowButtonId] = row.answered ?? 0;
          }
        }
        setAnsweredCounts(answeredMap);
      })
      .catch(() => {
        setSentCounts({});
        setAnsweredCounts({});
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [customerId, campaignId, filterDates, campaignCreatedAt]);

  const isFetching = fetchingNodes || isLoading;

  const rows = (flowButtonNodes ?? [])
    .filter((node) => node.flowButton?.id && node.parentNode?.whatsappTemplate?.id)
    .map((node) => ({
      flowButtonId: node.flowButton.id,
      whatsappTemplateId: node.parentNode.whatsappTemplate.id,
      templateName: node.parentNode.whatsappTemplate.whatsappTemplateName ?? "-",
    }));

  return (
    <>
      {isFetching && <LoadingSpinner />}
      {!isFetching && (
        <div className="max-h-[200px] overflow-y-auto">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Template</TableHeader>
                <TableHeader className="text-center">Sent</TableHeader>
                <TableHeader className="text-center">Answered</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length > 0 ? (
                rows.map((row) => (
                  <TableRow key={row.flowButtonId}>
                    <TableCell>{row.templateName}</TableCell>
                    <TableCell className="text-center">
                      {sentCounts[row.whatsappTemplateId] ?? 0}
                    </TableCell>
                    <TableCell className="text-center">
                      {answeredCounts[row.flowButtonId] ?? 0}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} className="text-center">
                    No WhatsApp flow
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}
